import React from 'react';
import { TrendingUp, Target, Calendar, Clock, Users, Award, AlertCircle, CheckCircle2 } from 'lucide-react';
import Button from '../../../components/ui/Button';

const PerformanceDashboard = ({ stats, reviewCycles, recentActivity }) => {
  const dashboardCards = [
    {
      title: 'Average Performance Rating',
      value: stats?.averageRating || '0.0',
      change: '+0.2',
      changeType: 'positive',
      icon: TrendingUp,
      subtitle: 'Out of 5.0'
    },
    {
      title: 'Goal Completion Rate',
      value: `${stats?.goalCompletionRate || 0}%`,
      change: '+5%',
      changeType: 'positive',
      icon: Target,
      subtitle: 'This quarter'
    },
    {
      title: 'Pending Reviews',
      value: stats?.pendingReviews || 0,
      change: '-3',
      changeType: 'positive',
      icon: Clock,
      subtitle: 'Awaiting completion'
    },
    {
      title: 'Upcoming Reviews',
      value: stats?.upcomingReviews || 0,
      change: '+2',
      changeType: 'neutral',
      icon: Calendar,
      subtitle: 'Next 7 days'
    }
  ];

  const activeReviewCycle = reviewCycles?.find(cycle => cycle?.status === 'active');

  return (
    <div className="space-y-6 mb-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardCards?.map((card, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <card.icon className="h-5 w-5 text-blue-600" />
                  <h3 className="text-sm font-medium text-gray-600">{card?.title}</h3>
                </div>
                <div className="flex items-baseline space-x-2">
                  <p className="text-2xl font-bold text-gray-900">{card?.value}</p>
                  <span className={`text-sm font-medium ${
                    card?.changeType === 'positive' ? 'text-green-600' :
                    card?.changeType === 'negative' ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {card?.change}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{card?.subtitle}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Active Review Cycle & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Review Cycle */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Calendar className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Current Review Cycle</h3>
            </div>

            {activeReviewCycle ? (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">{activeReviewCycle?.name}</h4>
                  <p className="text-sm text-gray-500">
                    {new Date(activeReviewCycle?.periodStart)?.toLocaleDateString()} - {new Date(activeReviewCycle?.periodEnd)?.toLocaleDateString()}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium text-gray-900">65%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '65%' }}></div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-gray-600">23 reviews completed</span>
                </div>

                <Button variant="outline" size="sm" className="w-full">
                  View Details
                </Button>
              </div>
            ) : (
              <div className="text-center py-6">
                <Calendar className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-4">No active review cycle</p>
                <Button size="sm">Start New Cycle</Button>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
              <Button variant="ghost" size="sm">View All</Button>
            </div>

            <div className="space-y-4">
              {recentActivity?.length > 0 ? (
                recentActivity?.map((activity, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50">
                    <div className="flex-shrink-0">
                      {activity?.reviewStatus === 'completed' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : activity?.reviewStatus === 'pending' ? (
                        <Clock className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Performance review {activity?.reviewStatus || 'updated'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Employee ID: {activity?.employeeId} • {activity?.lastReviewDate ? new Date(activity?.lastReviewDate)?.toLocaleDateString() : 'Today'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {activity?.currentRating ? `${activity?.currentRating}/5` : 'N/A'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Performance Insights Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            <Award className="h-8 w-8 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900">Performance Insights</h3>
            <p className="text-blue-700 mt-1">
              Team performance is up 12% this quarter. Consider recognizing top performers and providing additional support for those who need it.
            </p>
          </div>
          <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
            View Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;